"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FixedAmountDiscountPromotion = void 0;
const typings_1 = require("../../typings");
const utils_1 = require("../../utils");
const base_1 = require("../base");
class FixedAmountDiscountPromotion extends base_1.BasePromotion {
    constructor(props) {
        super(props);
        this.props = props;
    }
    /**
     * Apply fixed amount discount for selected product
     * e.g. 3 piece of selected products for 50฿
     * @param cart
     * @returns {Cart}
     */
    applyFixedAmountDiscountForSelectedProductPromotion(cart) {
        const isBundled = this.bundleSetting;
        if (isBundled) {
            throw new Error('Could not apply fixed amount discount promotion for X piece of selected product that is mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        // has been applied flag for checking whether the system should apply promotion again or not
        let hasBeenAppliedAndNonRepeatable = false;
        // total discount price for particular promotion
        let totalProductDiscount = 0;
        const newCart = Object.assign({}, cart);
        const productSKUsWithAppliedPromotion = newCart.productSKUs.map((productSKU) => {
            /**
             * If productSKU is free, don't take it into account
             * Skip it completely
             */
            if (productSKU.isFree) {
                return productSKU;
            }
            /**
             * Check whether previous product sku has been applied with
             * particular promotion or not, if yes, then proceed to skip
             * it after once
             */
            if (hasBeenAppliedAndNonRepeatable) {
                return productSKU;
            }
            const totalPriceOfCurrentProductSKU = productSKU.amount * productSKU.price;
            /**
             * SKU does not belong to promotion
             * calculate total price of current item
             * then proceed to next iteration
             */
            if (!this.selectedProductIds.includes(productSKU.id)) {
                return productSKU;
            }
            /**
             * case: SKU amount is less than threshold amount
             * e.g.
             * threshold amount: 3
             * SKU amount: 2, price: 30
             */
            const matchedCriteria = this.getCriteriaByTarget(productSKU.amount);
            if (!matchedCriteria) {
                return productSKU;
            }
            const discount = (0, utils_1.getPromotionDiscountCriteria)(matchedCriteria);
            if (!discount) {
                return productSKU;
            }
            /**
             * case: SKU amount is more than threshold amount
             * e.g.
             * threshold amount: 3
             * SKU amount: 5, price: 30
             */
            const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, productSKU.amount, this.isAutoGeneratedCriteria, this.isRepeatable);
            const totalDiscount = (0, utils_1.calculateDiscountableAmount)(multiplier, totalPriceOfCurrentProductSKU, discount);
            totalProductDiscount += totalDiscount;
            // Mark applied promotion for non-repeatable
            if (!this.isRepeatable) {
                hasBeenAppliedAndNonRepeatable = true;
            }
            return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions, { totalDiscount }) });
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount: totalProductDiscount });
    }
    /**
     * Apply fixed amount discount for selected product in bundle
     * e.g. 3 piece of mix selected product for 50฿
     * @param cart
     * @returns {Cart}
     */
    applyFixedAmountDiscountForSelectedProductInBundlePromotion(cart) {
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply fixed amount discount promotion for X piece of selected product in bundle that is not mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        // Total amount of products in shopping cart that are selected products
        const totalAmountOfSelectedProductsInShoppingCart = (0, utils_1.getTotalAmountOfSelectedProducts)(cart.productSKUs, this.selectedProductIds);
        const matchedCriteria = this.getCriteriaByTarget(totalAmountOfSelectedProductsInShoppingCart);
        if (!matchedCriteria) {
            return cart;
        }
        const discount = (0, utils_1.getPromotionDiscountCriteria)(matchedCriteria);
        if (!discount) {
            return cart;
        }
        // total discount price for particular promotion
        const newCart = Object.assign({}, cart);
        /**
         * Total amount of selected products in shopping cart is more than
         * or equals to threshold amount (Match promotion threshold)
         */
        const totalPriceOfMatchedProductSKUs = (0, utils_1.getTotalPriceOfSelectedProducts)(newCart.productSKUs, this.selectedProductIds);
        const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, totalAmountOfSelectedProductsInShoppingCart, this.isAutoGeneratedCriteria, this.isRepeatable);
        const totalDiscount = (0, utils_1.calculateDiscountableAmount)(multiplier, totalPriceOfMatchedProductSKUs, discount);
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
            totalDiscount,
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount });
    }
    /**
     * Apply fixed amount discount for selected product in total of X price
     * e.g. 200B of selected product to get 50฿
     * @param cart
     * @returns {Cart}
     */
    applyFixedAmountDiscountInTotalOfSelectedProductInBundlePromotion(cart) {
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply fixed amount discount promotion for total X price of selected product in bundle that is not mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        /**
         * Total price of selected products in shopping cart is more than
         * or equals to threshold price (Match promotion threshold)
         */
        const totalPriceOfMatchedProductSKUs = (0, utils_1.getTotalPriceOfSelectedProducts)(cart.productSKUs, this.selectedProductIds);
        const matchedCriteria = this.getCriteriaByTarget(totalPriceOfMatchedProductSKUs);
        if (!matchedCriteria) {
            return cart;
        }
        const discount = (0, utils_1.getPromotionDiscountCriteria)(matchedCriteria);
        if (!discount) {
            return cart;
        }
        // total discount price for particular promotion
        const newCart = Object.assign({}, cart);
        const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, totalPriceOfMatchedProductSKUs, this.isAutoGeneratedCriteria, this.isRepeatable);
        const totalDiscount = (0, utils_1.calculateDiscountableAmount)(multiplier, totalPriceOfMatchedProductSKUs, discount);
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs, {
            totalDiscount,
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), { totalDiscount });
    }
    /**
     * Apply promotion
     * @param cart
     * @returns {Cart}
     */
    applyPromotion(cart) {
        switch (this.settingCondition) {
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT:
                return this.applyFixedAmountDiscountForSelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyFixedAmountDiscountForSelectedProductInBundlePromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyFixedAmountDiscountInTotalOfSelectedProductInBundlePromotion(cart);
            default:
                return cart;
        }
    }
}
exports.FixedAmountDiscountPromotion = FixedAmountDiscountPromotion;
//# sourceMappingURL=fixed-amount.js.map